import React, {
  createContext,
  useState,
  useEffect,
  FormEvent,
  RefObject,
} from 'react';
import UtilInterlinkApi from '@dia-ui/util_interlink-api';
import UtilI18N from '@dia-ui/util_i18n';
import ROUTES, { ROUTE_CHILDREN, RouteItemInternal, RouteItems } from "./../routes";

import ServiceAuth from '../services/AuthService';
import ServiceUser from '../services/UserService';
import ClientService from '../services/ClientService';
import PartnerService from '../services/PartnerService';
import PartnerPartnerService from '../services/PartnerPartnerService';
import PartnerClientService from '../services/PartnerClientService';
import PartnerFileService from '../services/PartnerFileService';
import SystemService from '../services/SystemService';
import InfluxService from '../services/InfluxService';
import TwoFactorService from '../services/TwoFactorService';
import { type BreadcrumbType } from "../components/Breadcrumb";

/**
 * api
 */
let Api: UtilInterlinkApi = new UtilInterlinkApi(
  "http://localhost:30100/v1",
  "/refresh"
);

/**
 * services
 */
const services = {
  serviceAuth: new ServiceAuth,
  serviceUser: new ServiceUser,
  serviceClient: new ClientService,
  servicePartner: new PartnerService,
  servicePartnerPartner: new PartnerPartnerService,
  servicePartnerClient: new PartnerClientService,
  servicePartnerFile: new PartnerFileService,
  serviceSystem: new SystemService,
  serviceInflux: new InfluxService,
  serviceTwoFactor: new TwoFactorService
};

const AppContextObject = {
  // enums
  enums: {},
  loadEnums: async () => {},
  // services
  services: services,
  // loading
  isLoading: false,
  setLoading: (state: boolean) => {},
  // loading progress
  loadingProgress: 0,
  setLoadingProgress: (value: number) => {},
  // login
  isLoggedIn: (): boolean => {
    return false;
  },
  // i18n
  I18N: new UtilI18N,
  I18NisReady: false,
  I18NforceUpdate: () => {},
  I18NLocales: new Array<string>(),
  // DOM Event helper
  useOnDomEvent: (eventName: string, handler: (event: React.FormEvent<HTMLInputElement>) => void): React.RefObject<unknown> => React.createRef(),
  // ROUTES
  getRoutes: (): RouteItems => { return {} },
  getRouteChildren: (): RouteItemInternal[] => { return [] },
  // Breadcrumb
  breadcrumbs: new Array<BreadcrumbType>(),
  setBreadcrumbs: (breadcrumbs: BreadcrumbType[]): void => {},
  getBreadcrumbs: (): BreadcrumbType[] => { return [] }
};

/**
 * default context
 */
const AppContext= createContext(AppContextObject);

/**()
 * provider
 */
const { Provider } = AppContext;

/**
 * AppProvider
 * @param children
 * @constructor
 */
const AppProvider = ({ children }) => {
  /**
   * track progress of download requests to update progress value of loading indicator
   */
  Api.setProgressHandler(async (event: CustomEventInit) => setProgress(event.detail));

  /**
   * we intercept api request and response and handle loading state centralized
   */
  Api.setRequestInterceptor({
    success: () => setLoading(true),
    error: () => setLoading(false)
  });

  Api.setResponseInterceptor({
    success: () => setLoading(false),
    error: () => setLoading(false)
  });

  /**
   * useEffect
   */
  useEffect(() => {
    loadEnums();
    loadI18N();
  // eslint-disable-next-line
  }, []);

  /**
   * I18NforceUpdate
   */
  const [, updateState] = React.useState();
  //@ts-ignore
  const I18NforceUpdate = React.useCallback(() => updateState({}), []);

  /**
   * enums
   */
  const [enums, setEnums] = useState();

  /**
   * I18N
   */
  const [I18N, setI18N] = useState(new UtilI18N);

  /**
   * I18NisReady
   */
  const [I18NisReady, setI18NisReady] = useState(false);

  /**
   * I18NLocales
   */
  const [I18NLocales, setI18NLocales] = useState<string[]>([]);

  /**
   * isLoading
   */
  const [isLoading, setIsLoading] = useState(false);

  /**
   * loadingProgress
   */
  const [loadingProgress, setLoadingProgress] = useState(0);

  /**
   * breadcrumbs
   */
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbType[]>([]);

  /**
   * getBreadcrumbs
   */
  const getBreadcrumbs = (): BreadcrumbType[] => {
    return breadcrumbs;
  };

  /**
   * loadEnums proxy
   */
  const loadEnums = async () => {
    let enums = await services.serviceSystem.getAllEnums();

    setEnums(enums);
  };

  /**
   * loadI18N proxy
   */
  const loadI18N = async () => {
    /**
     * i18n
     */
    let _I18N: UtilI18N = new UtilI18N();
    await _I18N.initialize(I18N.getLocale(), '/assets/i18n/', true);

    /**
     * register locales
     */
    setI18NLocales(_I18N.getLocales());

    //@ts-ignore
    _I18N.observeOwnerDocumentLocale(document, () => setTimeout(() => I18NforceUpdate(), 500));

    setI18N(_I18N);
    setI18NisReady(true);
  };

  /**
   * setLoading proxy
   * @param state
   */
  const setLoading = (state: boolean): void => {
    setTimeout(() => setIsLoading(state), !state ? 1000 : 0);
  };

  /**
   * setProgress proxy
   * @param value
   */
  const setProgress = (value: number): void => {
    setLoadingProgress(value);
  };

  /**
   * isLoggedIn proxy
   */
  const isLoggedIn = (): boolean => {
    return services.serviceAuth.isLoggedIn();
  };

  /**
   * useOnDomEvent
   * @param eventName
   * @param handler
   */
  const useOnDomEvent = (eventName: string, handler: (event: FormEvent<HTMLInputElement>) => void): RefObject<unknown> => {
    const ref = React.createRef();

    React.useEffect(() => {
      // @ts-ignore
      ref.current.addEventListener(eventName, handler);
    // eslint-disable-next-line
    }, []);

    return ref;
  };

  /**
   * getRoutes
   */
  const getRoutes = (): RouteItems => {
    return ROUTES(I18N, I18NisReady);
  };

  /**
   * getRouteChildren
   */
  const getRouteChildren = (): RouteItemInternal[] => {
    return ROUTE_CHILDREN(I18N, I18NisReady);
  };

  return <Provider value={{
    // @ts-ignore
    enums,
    loadEnums,
    services,
    isLoading,
    setLoading,
    loadingProgress,
    setProgress,
    isLoggedIn,
    I18N,
    I18NisReady,
    I18NforceUpdate,
    I18NLocales,
    useOnDomEvent,
    getRoutes,
    getRouteChildren,
    breadcrumbs,
    setBreadcrumbs,
    getBreadcrumbs
  }}>{ children }</Provider>;
};

/**
 * AppContextObject type
 */
type AppContextType = typeof AppContextObject;

/**
 * exports
 */
export {
  type AppContextType,
  AppContext,
  AppProvider,
  Api
}
